import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client.ts";
import type {
  CampaignRow,
  Capacity,
  DomainPerformance,
  Overview,
  Reactivation,
} from "../api/types.ts";
import { useSendingIdentity } from "../components/SendingIdentity.tsx";
import {
  BrandBadge,
  Card,
  Empty,
  EngagementCaveat,
  Kpi,
  ListBody,
  StatusPill,
} from "../components/ui.tsx";

const pct = (n: number, of: number) => (of > 0 ? `${((n / of) * 100).toFixed(1)}%` : "—");

export function DashboardPage() {
  const [days, setDays] = useState(7);
  const { brandId } = useSendingIdentity();

  // One suffix for every query, so the whole page is scoped to the same
  // company and nothing is left reporting across all of them.
  const scope = brandId ? `&brandId=${brandId}` : "";

  const overview = useQuery({
    queryKey: ["overview", days, brandId],
    queryFn: () => api.get<Overview>(`/analytics/overview?days=${days}${scope}`),
  });
  const campaigns = useQuery({
    queryKey: ["campaign-rows", brandId],
    queryFn: () => api.get<CampaignRow[]>(`/analytics/campaigns?limit=8${scope}`),
  });
  const domains = useQuery({
    queryKey: ["domain-performance", days, brandId],
    queryFn: () => api.get<DomainPerformance[]>(`/analytics/domains?days=${days}${scope}`),
  });
  const capacity = useQuery({
    queryKey: ["capacity", brandId],
    queryFn: () =>
      api.get<Capacity[]>(`/analytics/capacity${brandId ? `?brandId=${brandId}` : ""}`),
    refetchInterval: 60_000,
  });

  /**
   * Only meaningful once a Pulse connection has synced, so the panel below
   * hides itself rather than showing an empty funnel to someone who has not
   * connected anything.
   */
  const reactivation = useQuery({
    queryKey: ["reactivation"],
    queryFn: () => api.get<Reactivation>("/pulse/reactivation"),
  });

  const o = overview.data;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>
          {brandId
            ? "Sending performance for the company selected above."
            : "Sending performance across every company."}
        </p>
      </div>

      <div className="toolbar">
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            type="button"
            className={`btn ${days === d ? "" : "ghost"} small`}
            onClick={() => setDays(d)}
          >
            {d} days
          </button>
        ))}
      </div>

      <div className="kpi-grid">
        <Kpi label="Delivered" value={o ? o.sent.toLocaleString() : "—"} />
        <Kpi
          label="Open rate"
          value={o ? o.openRate : "—"}
          unit="%"
          note={o ? `${o.opened.toLocaleString()} recipients` : undefined}
        />
        <Kpi
          label="Click rate"
          value={o ? o.clickRate : "—"}
          unit="%"
          note={o ? `${o.clicked.toLocaleString()} recipients` : undefined}
        />
        <Kpi
          label="Reply rate"
          value={o ? o.replyRate : "—"}
          unit="%"
          note={o ? `${o.replied.toLocaleString()} replies` : undefined}
        />
        <Kpi
          label="Bounces"
          value={o ? o.bounced.toLocaleString() : "—"}
          note={o ? `${o.bounceRate}% of delivered` : undefined}
          /* Above roughly 2%, receivers start treating the domain as a problem. */
          tone={o && o.bounceRate > 2 ? "bad" : undefined}
        />
        <Kpi label="Unsubscribes" value={o ? o.unsubscribed.toLocaleString() : "—"} />
      </div>

      <EngagementCaveat />

      <PulseHealth />

      <div className="grid-2">
        <Card title="Recent campaigns" action={<Link to="/campaigns">See all →</Link>}>
          <ListBody
            loading={campaigns.isLoading}
            empty={campaigns.data?.length === 0}
            emptyText="Nothing sent yet."
          >
            <table>
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th className="num">Sent</th>
                  <th className="num">Open</th>
                  <th className="num">Click</th>
                  <th className="num">Replies</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.data?.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <BrandBadge badge={c.brand_badge} color={c.brand_color} />
                        <div>
                          <div style={{ fontWeight: 600 }}>{c.name}</div>
                          <div style={{ color: "var(--gray-500)", fontSize: 11 }}>
                            {c.type} · {c.brand_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="num">{c.sent ? c.sent.toLocaleString() : "—"}</td>
                    <td className="num">{pct(c.opened, c.sent)}</td>
                    {/* A count rather than a rate: replies are few enough that
                        the number is the useful figure, and it is the one
                        signal here that is not distorted by privacy proxies. */}
                    <td className="num" style={{ fontWeight: c.replied > 0 ? 700 : 400 }}>
                      {c.replied ? c.replied.toLocaleString() : "—"}
                    </td>
                    <td>
                      <StatusPill status={c.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ListBody>
        </Card>

        <div>
          <Card title={`Domain performance · ${days}d`}>
            <ListBody
              loading={domains.isLoading}
              empty={domains.data?.length === 0}
              emptyText="No domains configured."
            >
              <table>
                <tbody>
                  {domains.data?.map((d) => (
                    <tr key={d.id}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <BrandBadge badge={d.brand_badge} color={d.brand_color} />
                          <div>
                            <div className="mono" style={{ fontWeight: 600, fontSize: 12 }}>
                              {d.name}
                            </div>
                            <div style={{ color: "var(--gray-500)", fontSize: 11 }}>
                              {d.stream}
                              {d.is_burnable && " · burnable"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="num">
                        <div>{pct(d.opened, d.sent)}</div>
                        <div style={{ color: "var(--gray-500)", fontWeight: 500 }}>
                          {d.sent.toLocaleString()} sent
                        </div>
                      </td>
                      <td className="num">
                        <div style={{ fontWeight: d.replied > 0 ? 700 : 400 }}>
                          {d.replied ? d.replied.toLocaleString() : "—"}
                        </div>
                        <div style={{ color: "var(--gray-500)", fontWeight: 500 }}>replies</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ListBody>
          </Card>

          {/*
            The number people actually need when a campaign is going slower than
            expected: how much of today's allowance is left.
          */}
          <Card title="Today's capacity" subtitle="Per sender, after warmup">
            <ListBody
              loading={capacity.isLoading}
              empty={capacity.data?.length === 0}
              emptyText="No senders configured."
            >
              <table>
                <tbody>
                  {capacity.data?.map((s) => (
                    <tr key={s.senderId}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{s.displayName}</div>
                        <div className="mono" style={{ color: "var(--gray-500)", fontSize: 11 }}>
                          {s.email}
                        </div>
                        <div className={`meter${s.remaining === 0 ? " full" : ""}`}>
                          <span style={{ width: `${s.cap ? (s.used / s.cap) * 100 : 0}%` }} />
                        </div>
                      </td>
                      <td className="num">
                        <div>
                          {s.used}/{s.cap}
                        </div>
                        {s.warming && <span className="pill info">Warming</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ListBody>
          </Card>
        </div>
      </div>

      {reactivation.data && reactivation.data.closedLeads > 0 && (
        <ReactivationPanel data={reactivation.data} />
      )}
    </div>
  );
}

/**
 * Did mailing dead leads bring any of them back.
 *
 * Split by the reason each cohort was closed, because that is the actionable
 * cut — whether a lead returns has more to do with why it was written off than
 * with anything in the email.
 *
 * "Reconverted" is a reply and nothing else. An open is shown beside it but
 * never counted as a return: Apple and Gmail prefetch pixels and security
 * gateways follow links, so an open-based number here would mostly measure
 * software.
 */
function ReactivationPanel({ data }: { data: Reactivation }) {
  const unmatched = data.closedLeads - data.matchedLeads;

  return (
    <Card
      title="Reactivation · closed leads from Pulse"
      subtitle={`${data.matchedLeads.toLocaleString()} of ${data.closedLeads.toLocaleString()} closed leads are reachable here`}
    >
      {data.cohorts.length === 0 ? (
        <Empty>No closed leads have been mailed yet.</Empty>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Closed because</th>
              <th className="num">Leads</th>
              <th className="num">Mailed</th>
              <th className="num">Engaged</th>
              <th className="num">Replied</th>
              <th className="num">Rate</th>
            </tr>
          </thead>
          <tbody>
            {data.cohorts.map((c) => (
              <tr key={c.reason}>
                <td>{c.reason}</td>
                <td className="num">{c.leads.toLocaleString()}</td>
                <td className="num">{c.mailed.toLocaleString()}</td>
                <td className="num">{c.engaged.toLocaleString()}</td>
                <td className="num" style={{ fontWeight: 700 }}>
                  {c.reconverted.toLocaleString()}
                </td>
                <td className="num">{pct(c.reconverted, c.mailed)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="card-body">
        <div className="hint">
          A reply is the only thing counted as a return — it is someone deciding to talk to you, and
          it arrives on the send that prompted it, so it cannot predate the campaign.
          {unmatched > 0 && (
            <>
              {" "}
              {unmatched.toLocaleString()} closed lead{unmatched === 1 ? "" : "s"} in Pulse
              {unmatched === 1 ? " has" : " have"} no contact here and cannot appear above.
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

interface PulseHealthData {
  connections: {
    id: string;
    name: string;
    is_active: boolean;
    last_synced_at: string | null;
    leads: number;
    errors: string[];
  }[];
  repliesSentToday: number;
  repliesFailedToday: number;
}

/** How long ago, in words a glance can take in. */
function ago(iso: string | null): string {
  if (!iso) return "never";
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours} h ago` : `${Math.round(hours / 24)} days ago`;
}

/**
 * Whether the Pulse link is working. The sync runs every five minutes, so a
 * last sync much older than that, or any error, means it has stopped —
 * worth seeing here, where people look every day, rather than only in
 * Settings. Nothing shows until Pulse is connected.
 */
function PulseHealth() {
  const health = useQuery({
    queryKey: ["pulse-health"],
    queryFn: () => api.get<PulseHealthData>("/pulse/health"),
    refetchInterval: 60_000,
    retry: false,
  });
  const data = health.data;
  if (!data || data.connections.length === 0) return null;

  return (
    <Card
      title="Pulse sync"
      subtitle={`${data.repliesSentToday.toLocaleString()} replies sent to Pulse today${
        data.repliesFailedToday ? ` · ${data.repliesFailedToday} could not be filed` : ""
      }`}
      action={
        <Link className="btn ghost small" to="/settings/pulse">
          Settings
        </Link>
      }
    >
      <table>
        <tbody>
          {data.connections.map((c) => {
            const stale =
              !c.last_synced_at || Date.now() - new Date(c.last_synced_at).getTime() > 20 * 60_000;
            const tone = !c.is_active ? "mute" : c.errors.length ? "bad" : stale ? "warn" : "ok";
            const label = !c.is_active
              ? "Paused"
              : c.errors.length
                ? "Errors"
                : stale
                  ? "Behind"
                  : "Healthy";
            return (
              <tr key={c.id}>
                <td>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  {c.errors.map((e) => (
                    <div key={e} style={{ color: "var(--red)", fontSize: 11 }}>
                      {e}
                    </div>
                  ))}
                </td>
                <td>
                  <span className={`pill ${tone}`}>{label}</span>
                </td>
                <td className="mono">Last sync {ago(c.last_synced_at)}</td>
                <td className="num">{c.leads.toLocaleString()} leads</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}
